import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import EditableImage from "../../core/EditableImage";
import {
    PiPlus,
    PiTrash,
    PiLinkedinLogo,
    PiTwitterLogo,
    PiInstagramLogo,
    PiEnvelope
} from "react-icons/pi";

interface TeamMember {
    id: string;
    name: string;
    role: string;
    bio: string;
    image: string;
    email?: string;
    linkedin?: string;
    twitter?: string;
    instagram?: string;
}

export default function TeamCircularCards({ content, onContentChange, isSelected }: SectionContentProps) {
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    const members: TeamMember[] = content.members || [
        {
            id: "1",
            name: "Sarah Johnson",
            role: "CEO",
            bio: "Leading our vision with passion and expertise.",
            image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop",
            email: "sarah@example.com",
            linkedin: "https://linkedin.com"
        },
        {
            id: "2",
            name: "Michael Chen",
            role: "CTO",
            bio: "Building robust technical foundations.",
            image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
            email: "michael@example.com",
            twitter: "https://twitter.com"
        },
        {
            id: "3",
            name: "Emily Rodriguez",
            role: "Design Lead",
            bio: "Crafting beautiful user experiences.",
            image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop",
            email: "emily@example.com",
            instagram: "https://instagram.com"
        },
        {
            id: "4",
            name: "David Kim",
            role: "Lead Developer",
            bio: "Turning ideas into code reality.",
            image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&h=400&fit=crop",
            email: "david@example.com",
            linkedin: "https://linkedin.com"
        }
    ];

    const addMember = () => {
        const newMember = {
            id: Date.now().toString(),
            name: "New Member",
            role: "Role",
            bio: "Brief description.",
            image: "",
            email: "",
            linkedin: "",
            twitter: ""
        };
        onContentChange?.({ ...content, members: [...members, newMember] });
    };

    const removeMember = (idx: number) => {
        const newMembers = members.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, members: newMembers });
    };

    const updateMember = (idx: number, field: string, value: string) => {
        const newMembers = [...members];
        newMembers[idx] = { ...newMembers[idx], [field]: value };
        onContentChange?.({ ...content, members: newMembers });
    };

    return (
        <div className={`bg-gray-900 px-8 py-20 ${isSelected ? "ring-2 ring-blue-500" : ""}`} style={{ fontFamily }}>
            <div className="mx-auto max-w-7xl">
                <div className="mb-16 text-center">
                    <h2 className="mb-4 text-4xl font-bold text-white">
                        <EditableText
                            value={content.title || "Our Team"}
                            onChange={(val) => onContentChange?.({ ...content, title: val })}
                            as="span"
                            placeholder="Section Title"
                        />
                    </h2>
                    <p className="mx-auto max-w-2xl text-lg text-gray-400">
                        <EditableText
                            value={content.subtitle || "Meet the experts behind our success"}
                            onChange={(val) => onContentChange?.({ ...content, subtitle: val })}
                            as="span"
                            placeholder="Section subtitle"
                        />
                    </p>
                </div>
                <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
                    {members.map((member, idx) => (
                        <div key={member.id || idx} className="group relative flex flex-col items-center text-center bg-gray-800/50 rounded-2xl p-8 transition-all duration-300 hover:bg-gray-800">
                            {isSelected && (
                                <button onClick={(e) => { e.stopPropagation(); removeMember(idx); }} className="absolute -top-3 -right-3 hidden group-hover:flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-500 z-10">
                                    <PiTrash className="h-4 w-4" />
                                </button>
                            )}
                            <div className="relative w-32 h-32 mb-6">
                                <div className="absolute inset-0 bg-blue-500 rounded-full opacity-20 blur-xl group-hover:opacity-30 transition-opacity" />
                                <EditableImage value={member.image} onChange={(val) => updateMember(idx, "image", val)} alt={member.name} className="w-full h-full rounded-full object-cover border-4 border-gray-700 relative z-10" width={128} height={128} />
                            </div>
                            <h3 className="text-xl font-bold text-white mb-1">
                                <EditableText value={member.name} onChange={(val) => updateMember(idx, "name", val)} as="span" placeholder="Name" />
                            </h3>
                            <p className="text-sm font-medium text-blue-400 mb-3">
                                <EditableText value={member.role} onChange={(val) => updateMember(idx, "role", val)} as="span" placeholder="Role" />
                            </p>
                            <p className="text-sm text-gray-400 mb-4">
                                <EditableText value={member.bio} onChange={(val) => updateMember(idx, "bio", val)} as="span" placeholder="Bio" />
                            </p>
                            <div className="flex items-center gap-2 mt-auto">
                                {member.email && <a href={`mailto:${member.email}`} className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-colors"><PiEnvelope className="w-4 h-4" /></a>}
                                {member.linkedin && <a href={member.linkedin} target="_blank" className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-colors"><PiLinkedinLogo className="w-4 h-4" /></a>}
                                {member.twitter && <a href={member.twitter} target="_blank" className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-full transition-colors"><PiTwitterLogo className="w-4 h-4" /></a>}
                            </div>
                        </div>
                    ))}
                    {isSelected && (
                        <button onClick={addMember} className="flex flex-col items-center justify-center min-h-[300px] rounded-2xl border-2 border-dashed border-gray-600 bg-gray-800/30 text-gray-500 hover:border-blue-400 hover:bg-gray-800 hover:text-blue-400 transition-all">
                            <PiPlus className="h-10 w-10 mb-2" />
                            <span className="text-lg font-medium">Add Member</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
